/**
 * The one call interface the softphone (GlobalVoiceProvider) talks to, so it
 * does not branch on the carrier everywhere. Twilio (default) and Telnyx
 * (per number, chosen on the server) each wrap their SDK's call object in it.
 *
 * Events are normalised:
 *   ringing   outbound: the far end is ringing
 *   accepted  the call is up (answered by us, or by the far end)
 *   ended     exactly once, with why (see EndReason)
 *   warning / warning-cleared   network-quality hints (Twilio only)
 */
import type { EndReason, VoiceProvider } from './callSlots';

export interface VoiceCallEvents {
  ringing: () => void;
  accepted: () => void;
  ended: (reason: EndReason, error?: any) => void;
  warning: (name: string) => void;
  'warning-cleared': (name: string) => void;
}

export interface VoiceCall {
  readonly provider: VoiceProvider;
  /** The SDK object (Twilio Call / Telnyx Call), for legacy callers of callRef. */
  readonly raw: any;
  /** Server call id when known (Twilio CA…, Telnyx v3:…). */
  callSid(): string | undefined;
  accept(): void;
  /** Decline a ringing inbound call. */
  reject(): void;
  hangup(): void;
  mute(muted: boolean): void;
  isMuted(): boolean;
  sendDigits(digits: string): void;
  /**
   * The server says this ringing call is over (the caller hung up) though the
   * SDK did not report it: stop ringing locally and end it as 'canceled'.
   */
  dismiss(): void;
  /** True once the call can no longer be answered (ended, or the SDK closed it). */
  isGone(): boolean;
  /** Media-only hold in the SDK, for calls the server cannot hold. */
  localHold?(on: boolean): Promise<void>;
  on<E extends keyof VoiceCallEvents>(event: E, cb: VoiceCallEvents[E]): void;
}

/** Tiny typed emitter shared by the adapters; `ended` fires at most once. */
export class CallEmitter {
  private handlers: { [E in keyof VoiceCallEvents]?: VoiceCallEvents[E][] } = {};
  private endedOnce = false;
  private acceptedOnce = false;

  on<E extends keyof VoiceCallEvents>(event: E, cb: VoiceCallEvents[E]) {
    (this.handlers[event] ||= [] as any).push(cb);
  }

  emit<E extends keyof VoiceCallEvents>(event: E, ...args: Parameters<VoiceCallEvents[E]>) {
    if (event === 'ended') {
      if (this.endedOnce) return;
      this.endedOnce = true;
    }
    if (event === 'accepted') {
      if (this.acceptedOnce || this.endedOnce) return;
      this.acceptedOnce = true;
    }
    for (const cb of this.handlers[event] || []) {
      try { (cb as any)(...args); } catch (e) { console.error(`[VOICE] ${event} handler failed:`, e); }
    }
  }

  get ended() { return this.endedOnce; }
  get accepted() { return this.acceptedOnce; }
}

/** Twilio Voice SDK 2.x Call → VoiceCall. */
export class TwilioVoiceCall implements VoiceCall {
  readonly provider = 'twilio' as const;
  private em = new CallEmitter();
  private localEnd: EndReason | null = null;
  private sid: string | undefined;

  constructor(readonly raw: any) {
    this.sid = raw?.parameters?.CallSid || undefined;
    raw.on('ringing', () => this.em.emit('ringing'));
    raw.on('accept', () => {
      this.sid = raw?.parameters?.CallSid || this.sid;
      this.em.emit('accepted');
    });
    raw.on('disconnect', () => this.em.emit('ended', this.localEnd || 'remote'));
    raw.on('cancel', () => this.em.emit('ended', 'canceled'));
    raw.on('reject', () => this.em.emit('ended', 'rejected'));
    raw.on('error', (err: any) => this.em.emit('ended', 'error', err));
    raw.on('warning', (n: string) => this.em.emit('warning', n));
    raw.on('warning-cleared', (n: string) => this.em.emit('warning-cleared', n));
  }

  callSid() { return this.sid; }
  accept() { this.raw.accept(); }
  dismiss() {
    // ignore() silences the SDK ringtone without sending a reject upstream.
    try { this.raw.ignore?.(); } catch { /* already closed */ }
    this.em.emit('ended', 'canceled');
  }
  isGone() {
    if (this.em.ended) return true;
    try { return this.raw.status?.() === 'closed'; } catch { return false; }
  }
  reject() {
    this.localEnd = 'rejected';
    this.raw.reject();
    // The SDK does not always emit 'reject' for a local reject.
    this.em.emit('ended', 'rejected');
  }
  hangup() {
    this.localEnd = 'hangup';
    try { this.raw.disconnect(); } finally { this.em.emit('ended', 'hangup'); }
  }
  mute(m: boolean) { this.raw.mute(m); }
  isMuted() { return !!this.raw.isMuted?.(); }
  sendDigits(d: string) { this.raw.sendDigits(d); }
  on<E extends keyof VoiceCallEvents>(e: E, cb: VoiceCallEvents[E]) { this.em.on(e, cb); }
}
